/* eslint-disable @typescript-eslint/no-explicit-any */
import { getLog } from '@eigtech/ui-shared-logging'
import { Button, ButtonProps } from '@eigtech/ui/components/button'
import { zodResolver } from '@hookform/resolvers/zod'
import { JSX, useCallback, useRef } from 'react'
import {
  FieldValues,
  UseFormReturn as RHFUseFormReturn,
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormProps,
  useForm as useBaseForm,
} from 'react-hook-form'
import { z } from 'zod'
import { Form as BaseForm, FormProps } from './components/Form'

const log = getLog()

export function useForm<
  T extends z.Schema<any, any>,
  TContext = any,
  TTransformedValues = z.output<T>,
>(
  schema: T,
  propsId: string,
  props?: UseFormProps<z.input<T>, TContext, TTransformedValues>
): FormApi<z.input<T>, TContext, TTransformedValues> {
  // zod v4 distinguishes input/output types, and @hookform/resolvers v5's
  // zodResolver yields `Resolver<input<T>, unknown, output<T>>`. Field values are
  // the pre-transform input; transformed values are the parsed output.
  type TFieldValues = z.input<T>

  const id = useRef(propsId)

  const { current: resolver } = useRef(zodResolver(schema))

  const form = useBaseForm<TFieldValues, TContext, TTransformedValues>({
    resolver: resolver,
    mode: 'onTouched',
    ...props,
  })

  const handleSubmit: UseFormHandleSubmit<TFieldValues, TTransformedValues> = useCallback(
    (onValid, onInvalid) =>
      form.handleSubmit(async (data, event) => {
        try {
          return await onValid(data, event)
        } catch (error) {
          log.error(`could not submit form: ${id.current}`, { error })
        }
      }, onInvalid),
    [form]
  )

  const SubmitButton = useCallback(
    (props: Omit<ButtonProps, 'form'>) => {
      const {
        formState: { isSubmitting },
      } = form

      return (
        <Button
          form={id.current}
          id={`${id.current}-submit`}
          isLoading={isSubmitting}
          type="submit"
          variant="primary"
          {...props}
        >
          {props.children ?? 'Submit'}
        </Button>
      )
    },
    [form]
  )

  const SubmitIconButton = useCallback(
    (props: ButtonProps) => {
      const {
        formState: { isSubmitting },
      } = form

      return (
        <Button
          form={id.current}
          id={`${id.current}-submit`}
          isLoading={isSubmitting}
          mode="icon"
          type="submit"
          {...props}
        />
      )
    },
    [form]
  )

  const Form = useCallback(
    <TOnSubmit extends SubmitHandler<TTransformedValues>>(
      props: Omit<
        FormProps<TFieldValues, TContext, TTransformedValues>,
        'id' | 'form' | 'onSubmit'
      > & {
        onSubmit: TOnSubmit
      }
    ) => (
      <BaseForm form={form} id={id.current} {...props} onSubmit={handleSubmit(props.onSubmit)} />
    ),
    [form, handleSubmit]
  )

  return {
    ...form,
    Form,
    SubmitButton,
    SubmitIconButton,
    handleSubmit,
  }
}

export type UseFormReturn<
  T extends z.Schema<any, any>,
  TContext = any,
  TTransformedValues = z.infer<T>,
> = ReturnType<typeof useForm<T, TContext, TTransformedValues>>

/**
 * The shape returned by {@link useForm}, keyed on the form's field/transformed
 * value types rather than the zod schema. Components that receive a `form` (e.g.
 * FormDrawer/FormModal) should be generic over these so TypeScript can infer them
 * from the passed `form` — the schema `T` is not inferable from a form value, so
 * typing such props as `UseFormReturn<T>` collapses `T` to `z.ZodType<any, any>`
 * and breaks assignability (field values widen to `any`).
 */
export type FormApi<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
> = RHFUseFormReturn<TFieldValues, TContext, TTransformedValues> & {
  Form: <TOnSubmit extends SubmitHandler<TTransformedValues>>(
    props: Omit<
      FormProps<TFieldValues, TContext, TTransformedValues>,
      'id' | 'form' | 'onSubmit'
    > & {
      onSubmit: TOnSubmit
    }
  ) => JSX.Element
  SubmitButton: (props: Omit<ButtonProps, 'form'>) => JSX.Element
  SubmitIconButton: (props: ButtonProps) => JSX.Element
}
